import { AfterViewInit, Component, ElementRef, Input, OnInit, Renderer2, ViewChild } from '@angular/core';
import { AnnouncementDisplayType, AnnouncementModel } from '@fgb/core';
import { Subject } from 'rxjs';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { takeUntil } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { ScreenType } from '@fgb/portal-component-library/src/lib/directives';

@Component({
  selector: 'fgbcl-announcement-content',
  templateUrl: './announcement-content.component.html',
  styleUrls: ['./announcement-content.component.scss'],
})
export class AnnouncementContentComponent implements OnInit, AfterViewInit {
  @Input() announcement: AnnouncementModel;
  @ViewChild('link') private _link: ElementRef<HTMLAnchorElement>;
  isDisabled: boolean;
  isEnableTarget: boolean = false;
  isComingSoon: boolean = false;
  /** Exposed so the template can pick the mobile or desktop layout. */
  ScreenType = ScreenType;

  /** Whether this announcement's display type includes a header, image or video. */
  get showHeader(): boolean {
    return this.announcement.DisplayType !== AnnouncementDisplayType.TextOnly;
  }

  /** Only the video display type plays a video, the rest fall back to the still image. */
  get showVideo(): boolean {
    return this.announcement.DisplayType === AnnouncementDisplayType.VideoAndText && this.announcement.VideoId !== null;
  }

  /**
   * Whether opening the card would show anything the collapsed strip does not already.
   *
   * Keyed off what is actually present rather than the declared display type, since an announcement
   * can carry a type its data does not match, a video type with no video and no description for
   * instance, and would otherwise be left inert with no way to open it.
   */
  get isExpandable(): boolean {
    return !!this.announcement.Description || this.showVideo || (this.showHeader && !!this.announcement.Image);
  }
  private _destroyed$ = new Subject<void>();

  constructor(private renderer: Renderer2, private breakpointObserver: BreakpointObserver) {}

  ngOnInit(): void {
    this.isDisabled = !!!this.announcement.LinkUrl;
    const comingSoonDate = DateTime.fromISO(this.announcement.ComingSoonDate as string, { zone: 'utc' });
    const requestTime = DateTime.fromISO(this.announcement.RequestTime as string, { zone: 'utc' });
    const itemStartDate = DateTime.fromISO(this.announcement.StartDate as string, { zone: 'utc' });

    this.isComingSoon = this.announcement.AllowComingSoon && (itemStartDate > requestTime || comingSoonDate > requestTime);

    // detect screen size changes
    this.breakpointObserver
      .observe(['(max-width: 991px)'])
      .pipe(takeUntil(this._destroyed$))
      .subscribe((result: BreakpointState) => {
        if (result.matches) {
          this.isEnableTarget = false;
        } else {
          this.isEnableTarget = true;
        }
      });
  }

  ngAfterViewInit(): void {
    this.checkLink();
  }

  checkLink(): void {
    if (this.isDisabled) {
      if (this._link) {
        this.renderer.addClass(this._link.nativeElement, 'disabled');
        this.renderer.setAttribute(this._link.nativeElement, 'aria-disabled', 'true');
        this.renderer.listen(this._link.nativeElement, 'click', (event) => {
          // While user click is disabled, keyboard event still able to click using enter key
          // Prevents entering using keyboard
          event.preventDefault();
        });
      }
      return;
    }
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}
